import { format } from 'date-fns';

export function formatBytes(bytes: number, decimals: number = 1) {
  if (!+bytes) {
    return '0 Bytes';
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GiB', 'TiB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export function formatGiBWithValueAndUnit(
  gibValue: number | undefined | null,
  decimals: number = 1
): { value: number | string; unit: string | null } {
  if (gibValue === null || gibValue === undefined || isNaN(gibValue)) {
    return { value: 'N/A', unit: null };
  }
  if (gibValue === 0) {
    return { value: 0, unit: 'Bytes' };
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];

  const i = Math.floor(Math.log(Math.abs(gibValue)) / Math.log(k));
  const unitIndex = Math.max(0, Math.min(i, sizes.length - 1));
  const convertedValue = gibValue / Math.pow(k, unitIndex);
  const formattedNum = parseFloat(convertedValue.toFixed(dm));

  return { value: formattedNum, unit: sizes[unitIndex] };
}

export function formatGiB(gibValue: number | undefined | null, decimals: number = 1): string {
  const { value, unit } = formatGiBWithValueAndUnit(gibValue, decimals);
  if (!unit) {
    return String(value);
  }
  return `${value} ${unit}`;
}

export function convertToGIB(value: number): string {
  const gigabytes = value / (1024 * 1024 * 1024);
  return `${gigabytes.toFixed(0)} GiB`;
}

export const PositiveDecimalRegex = /^(([1-9]\d*(\.\d+)?)|(\d{1,3}(,\d{3})*(\.\d+)?)|(0\.\d+))$/;

export const numValueOrDefault = (
  v: number | string | undefined | null,
  def: number,
  _debugName?: string
) => {
  const v1 = v === undefined || v === '' || v === null || isNaN(Number(v)) ? def : Number(v);
  //console.log({ v, v1, d: _debugName });
  return v1;
};

export function formatDate(date: string | Date, showTime?: boolean): string {
  return format(
    typeof date === 'string' ? new Date(date) : date,
    showTime ? 'yyyy/MM/dd | HH:mm' : 'yyyy/MM/dd'
  );
}

export const dollarComparator = (valueA: string, valueB: string) => {
  if (valueA === 'N/A' && valueB === 'N/A') {
    return 0;
  }
  if (valueA === 'N/A') {
    return 1;
  }
  if (valueB === 'N/A') {
    return -1;
  }
  const numA = parseFloat(valueA.replace('$', ''));
  const numB = parseFloat(valueB.replace('$', ''));
  return numA - numB;
};

export function formatVolumeCount(count: number | undefined | null): string {
  if (count === null || count === undefined || isNaN(count)) {
    return 'N/A Volumes';
  }
  return `${count.toLocaleString()} Volumes`;
}

export function formatVolumeMetric(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A';
  }
  return value.toLocaleString();
}

/**
 * Compact currency display for KPI savings values.
 * - Under $1,000: whole dollars, no cents
 * - $1,000–$9,999: K with one decimal
 * - $10,000–$999,999: K with no decimals
 * - $1,000,000–$9,999,999: M with one decimal
 * - $10,000,000+: M with no decimals
 */
export function formatCompactCurrency(value: number | undefined | null): string {
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A';
  }

  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (abs < 1000) {
    return `${sign}$${Math.round(abs)}`;
  }
  if (abs < 10_000) {
    const formatted = (abs / 1000).toFixed(1);
    const trimmed = formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted;
    return `${sign}$${trimmed}K`;
  }
  if (abs < 1_000_000) {
    return `${sign}$${Math.round(abs / 1000)}K`;
  }
  if (abs < 10_000_000) {
    const formatted = (abs / 1_000_000).toFixed(1);
    const trimmed = formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted;
    return `${sign}$${trimmed}M`;
  }
  return `${sign}$${Math.round(abs / 1_000_000)}M`;
}

export function formatStorageChangePercent(
  originalGiB: number | undefined,
  currentGiB: number | undefined
): string | null {
  if (
    originalGiB === undefined ||
    currentGiB === undefined ||
    isNaN(originalGiB) ||
    isNaN(currentGiB) ||
    originalGiB === 0
  ) {
    return null;
  }

  const change = Math.round(((currentGiB - originalGiB) / originalGiB) * 100);
  if (change === 0) {
    return '0%';
  }
  return change > 0 ? `+${change}%` : `${change}%`;
}

export function formatBytesWithValueAndUnit(bytes: number, decimals: number = 1) {
  if (bytes === null || bytes === undefined || isNaN(bytes)) {
    return { value: 'N/A', unit: null };
  }
  if (bytes === 0) {
    return { value: 0, unit: 'GiB' };
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));

  const unitIndex = Math.min(i, sizes.length - 1);

  const convertedValue = bytes / Math.pow(k, unitIndex);

  const numStr = convertedValue.toFixed(dm);
  const formattedNum = parseFloat(numStr);

  return { value: formattedNum, unit: sizes[unitIndex] };
}
